import { useMemo } from "react";
import { FilterGroup } from "../types";
import { filterGroups } from "../filterGroups";
import { useFilterFieldOptions } from "@/components/FilterSidebar/hooks/useFilterFieldsOptions";
import { filterSidebarContext } from "@/reactiveVars/global";
import { useReactiveVar } from "@apollo/client";
import { FilterContextMeta } from "@/lib/FilterContextMeta";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { PartnerFeatureEnum } from "@/__generated__/types";

export const useFilters = () => {
  const { options, loading } = useFilterFieldOptions();

  const filterContextVar = useReactiveVar(filterSidebarContext);
  const getFilters = FilterContextMeta[filterContextVar.data]?.getFilterGroups;
  const enabledFeatures = useOrgPermissions().getOrgFeatureFlags();

  const filters: FilterGroup[] | null = useMemo(() => {
    if (loading) return null;

    if (options) {
      // use custom filter groups if defined for this context
      const groups = getFilters ? getFilters() : filterGroups;

      // replace 'options' for each filter input with dynamically generated field options
      return removeDisabled(groups, enabledFeatures || []).map((group) => ({
        title: group.title,
        filters: group.filters.map((filter) =>
          filter.name in options
            ? {
                ...filter,
                options: options[filter.name as keyof typeof options],
              }
            : filter,
        ),
      }));
    }

    return null;
  }, [options, loading, getFilters, enabledFeatures]);

  return { filters, loading };
};

const removeDisabled = (
  groups: FilterGroup[],
  featureFlags: PartnerFeatureEnum[],
) =>
  groups
    .map((group) => ({
      ...group,
      filters: group.filters.filter(
        (filter) => !filter.isDisabled?.({ featureFlags }),
      ),
    }))
    .filter((group) => group.filters.length > 0);
